/* eslint-disable react/require-default-props */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { listUserDirectory } from '../../../../services/users/usersApi';
import { notifyApiError } from '../../../../utils/feedback';
import { formatUserLabel } from '../../../../utils/format';

// ---------------------------------------------------------------------------
// Seletor de entrevistador (opcional). <select> pré-carregado de usuários —
// escolhido em vez de autocomplete porque mostra o entrevistador já vinculado
// por id (ex.: ao EDITAR uma entrevista o valor aparece preenchido, então mudar
// só a data não exige reselecionar). Renderiza só o <select>.
//
// Fonte: `GET /users/directory` (handoff 0110 §3.9, 2026-09-15) — rota sem
// RBAC, criada pelo backend para os seletores de usuário fora da administração
// de IAM. Carrega **só ativos** (default da rota): ninguém deve ser marcado
// como entrevistador de uma conta que não existe mais.
//
// Contas excluídas continuam aparecendo em UM caso: quando o valor JÁ salvo
// aponta para uma delas. Sem isso o `value` não casaria com nenhuma opção, o
// select cairia em "Nenhum" e salvar outra coisa (só a data, por exemplo)
// apagaria o entrevistador em silêncio.
//
// Histórico: até 2026-09-15 lia a listagem administrativa (`iam:user:read`,
// Master-only) e ficava vazio para os papéis do RH — caveat resolvido
// backend-first pelo diretório.
// ---------------------------------------------------------------------------

export default function InterviewerPicker({
   id,
   value,
   onChange,
   disabled = false,
}) {
   const [users, setUsers] = useState([]);
   const [isLoading, setIsLoading] = useState(false);

   useEffect(() => {
      let ignore = false;
      setIsLoading(true);
      listUserDirectory()
         .then((list) => {
            if (!ignore) setUsers(list);
         })
         .catch((err) => {
            if (!ignore) {
               notifyApiError(err, {
                  fallback: 'Erro ao carregar os usuários.',
               });
            }
         })
         .finally(() => {
            if (!ignore) setIsLoading(false);
         });
      return () => {
         ignore = true;
      };
   }, []);

   // Entrevistador já salvo que não está entre os ativos: preserva o vínculo
   // (ver cabeçalho). Não temos o nome — o diretório de ativos não o traz —,
   // então o rótulo usa o id.
   const orphanValue = useMemo(() => {
      if (!value || isLoading) return null;
      const exists = users.some((user) => String(user.id) === String(value));
      return exists ? null : String(value);
   }, [value, users, isLoading]);

   return (
      <select
         id={id}
         value={value ?? ''}
         onChange={(e) => onChange(e.target.value)}
         disabled={disabled || isLoading}
      >
         <option value="">
            {isLoading ? 'Carregando…' : 'Nenhum (opcional)'}
         </option>
         {orphanValue && (
            <option value={orphanValue}>
               Usuário #{orphanValue} (inativo)
            </option>
         )}
         {users.map((user) => (
            <option key={user.id} value={String(user.id)}>
               {formatUserLabel(user)}
            </option>
         ))}
      </select>
   );
}

InterviewerPicker.propTypes = {
   id: PropTypes.string,
   value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   onChange: PropTypes.func.isRequired,
   disabled: PropTypes.bool,
};
